import React from 'react';

import DefaultExtensionProvider from '@atlaskit/editor-common/extensions/default-extension-provider';
import type { ExtensionManifest } from '@atlaskit/editor-common/extensions/extension-manifest';
import type { Parameters } from '@atlaskit/editor-common/extensions/extension-parameters';
import {
	ROVO_SKILL_EXTENSION_KEY,
	ROVO_SKILL_EXTENSION_TYPE,
} from '@atlassian/conversation-assistant-content-transformer/utils/skills';
import { defaultIcon } from '@atlassian/rovo-platform-ui-components/skills/skill-icons';

import { createSkillTagNodeRenderer } from './createSkillTagNodeRenderer';

/**
 * Provides a minimal extension provider that **only** registers the skill chip
 * node renderer — no quick-insert items, no skills data dependency.
 *
 * This is designed to be passed as `extensionProviders` to the Suspense
 * **fallback** editor so that skill chip `inlineExtension` nodes embedded in
 * `defaultValue` ADF render immediately, eliminating the visual lag that
 * occurred when the fallback editor had no extension providers and could not
 * render the chip until the full `ComposableEditorWithSkills` mounted.
 */
export const useSkillTagExtensionProvider = (
	siteId?: string,
	disableSkillTagHoverCardInEditor: boolean = false,
	skillTagHoverCardMaxHeightInEditor?: number | string,
): DefaultExtensionProvider<Parameters>[] => {
	return React.useMemo(() => {
		const manifest: ExtensionManifest = {
			title: 'Skills',
			type: ROVO_SKILL_EXTENSION_TYPE,
			key: ROVO_SKILL_EXTENSION_KEY,
			icons: {
				'16': defaultIcon,
				'24': defaultIcon,
				'48': defaultIcon,
			},
			modules: {
				quickInsert: [],
				nodes: createSkillTagNodeRenderer(
					siteId,
					disableSkillTagHoverCardInEditor,
					skillTagHoverCardMaxHeightInEditor,
				),
			},
		};

		return [new DefaultExtensionProvider<Parameters>([manifest])];
	}, [siteId, disableSkillTagHoverCardInEditor, skillTagHoverCardMaxHeightInEditor]);
};
